export interface PageConfig {
  /** Whether the page supports localization and should be available in multiple languages. */
  globalized?: boolean;

  /**
   * When set to false, this disables routeMetadata.json generation for the page.
   *
   * This is primarily useful when you also have the route defined in dot_com_routes.rb and are
   * migrating an existing Liquid page to React (as it avoids overlapping routes between React and Liquid
   * in the SFE config).
   */
  generateRoute?: false;

  /** Whether the page is allowed to be indexed by search engines. */
  indexed?: boolean;

  /**
   * Whether the page is internal-only and not accessible on the public site. If true, you
   * can view the page on mkt.corp.stripe.com but not stripe.com.
   */
  internal?: boolean;

  /**
   * Whether the page requires authentication to access. This feature can be used when testing a page
   * that is not yet ready to be publically accessible, but needs to be exposed on the public site to
   * verify behavior through Edge or from mobile devices.  It will require a querystring auth token
   * to be passed in the URL to protect the page from being accessed by the public.
   *
   * Example:
   * https://stripe.com/-internal/page-beta?__auth=<token>
   *
   * The service will verify querystring auth token against the list of allowed values in the
   * `mkt_auth_querystring_for_route` feature flag.
   */
  protected?: boolean;

  /** The UI engine to use for rendering the page. */
  uiEngine?: 'sail' | 'helm' | 'none';
}

export type RuntimePageConfig = Omit<Required<PageConfig>, 'generateRoute'>;

export const DEFAULT_PAGE_CONFIG: RuntimePageConfig = {
  globalized: true,
  indexed: true,
  internal: false,
  protected: false,
  uiEngine: 'helm',
};
